import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Chip,
  Button,
  Fade,
  Grow,
  useTheme,
  CardMedia,
  Icon,
  Grid,
  Stack,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/es';
import Tablero from '../components/Tablero';
import Loading from '../components/Loading';
import { io } from 'socket.io-client';

moment.locale('es');

const MiRifa = () => {
  const theme = useTheme();
  const { id, codigo } = useParams();
  const rifaParam = id || codigo;
  const navigate = useNavigate();
  const [dataRifa, setDataRifa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableroKey, setTableroKey] = useState(0);

  const numerosGanadores = useMemo(() => {
    if (!dataRifa?.boletos || !Array.isArray(dataRifa.boletos)) return [];
    return dataRifa.boletos.filter(
      (b) => b.ganador === '1' || b.ganador === true || b.ganador === 1
    );
  }, [dataRifa]);

  // Cargar datos de la rifa por ID o código público seguro
  const fetchRifaData = useCallback(async () => {
    if (!rifaParam) return;
    try {
      setLoading(true);
      setError(null);
      const apiUrl = process.env.REACT_APP_URL || 'https://apitest.rauhtech.com/v2/misrifas';
      const requestUrl = `${apiUrl}/rifas/mirifa/${rifaParam}`;
      const res = await axios.get(requestUrl);

      const rawData = res.data?.data !== undefined ? res.data.data : res.data;
      const rifaFinal = Array.isArray(rawData) ? rawData[0] : rawData;

      if (rifaFinal && rifaFinal.id) {
        setDataRifa(rifaFinal);
      } else {
        setError(res.data?.message || 'No pudimos encontrar la información de esta rifa.');
      }
    } catch (err) {
      console.error('[MiRifa] Error cargando rifa:', err);
      setError(
        err.response?.data?.message ||
        'Ocurrió un error al cargar la rifa. Por favor verifica el enlace e intenta nuevamente.'
      );
    } finally {
      setLoading(false);
    }
  }, [rifaParam]);

  useEffect(() => {
    if (rifaParam) {
      fetchRifaData();
    }
  }, [rifaParam, fetchRifaData]);

  // Conexión a WebSockets para sincronización en tiempo real
  useEffect(() => {
    if (!dataRifa?.id) return;

    const socketUrl = process.env.REACT_APP_SOCKET_URL || 'https://apitest.rauhtech.com';

    const socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
    });

    socket.on(`rifa_${dataRifa.id}`, () => {
      fetchRifaData();
    });

    return () => {
      socket.disconnect();
    };
  }, [dataRifa?.id, fetchRifaData]);

  const handleReservaSuccess = () => {
    fetchRifaData();
    setTableroKey((prev) => prev + 1);
  };

  const handleVolverInicio = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatPrice = (val) => {
    const num = Number(val) || 0;
    return `$ ${num.toLocaleString('es-CO')}`;
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading mensaje="Cargando información del sorteo..." />
      </Box>
    );
  }

  if (error || !dataRifa) {
    return (
      <Box sx={{ minHeight: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 6 },
            textAlign: 'center',
            borderRadius: '20px',
            maxWidth: 500,
            bgcolor: '#ffffff',
            border: '1px solid #e2e8f0',
            boxShadow: '0 12px 40px rgba(45, 52, 132, 0.08)',
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '16px',
              bgcolor: 'rgba(241, 91, 40, 0.08)',
              color: theme.palette.secondary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <Icon sx={{ fontSize: 36 }}>confirmation_number</Icon>
          </Box>
          <Typography variant="h5" sx={{ mb: 1.5, color: '#1e293b', fontWeight: 900 }}>
            Sorteo no disponible
          </Typography>
          <Typography variant="body2" sx={{ mb: 4, color: '#64748b', lineHeight: 1.7 }}>
            {error || 'No pudimos encontrar la información de este sorteo o el enlace no es válido.'}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={handleVolverInicio}
            startIcon={<Icon>arrow_back</Icon>}
            sx={{
              borderRadius: '10px',
              py: 1.4,
              px: 4,
              fontWeight: 800,
              borderWidth: '1.5px',
            }}
          >
            Volver al Inicio
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background:
          theme.palette.mode === 'dark'
            ? 'radial-gradient(ellipse at 50% 0%, #151b2e 0%, #0b0f19 60%, #060911 100%)'
            : 'radial-gradient(ellipse at 50% 0%, #eef2ff 0%, #f8fafc 60%, #f1f5f9 100%)',
        minHeight: '100vh',
        pt: { xs: '76px', sm: '88px', md: '92px' },
        pb: { xs: 3, md: 4 },
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={{ xs: 2, lg: 2.5 }} alignItems="flex-start">
          {/* ================= COLUMNA IZQUIERDA: DETALLES DE LA RIFA ================= */}
          <Grid item xs={12} lg={5}>
            <Fade in timeout={400}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '14px',
                  overflow: 'hidden',
                  bgcolor: theme.palette.background.paper,
                  border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                  boxShadow: theme.palette.mode === 'dark' ? '0 8px 24px rgba(0, 0, 0, 0.4)' : '0 8px 24px rgba(45, 52, 132, 0.04)',
                }}
              >
                {/* 1. Portada */}
                <Box
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    height: { xs: 140, sm: 160, md: 170 },
                    bgcolor: theme.palette.primary.main,
                    background: 'linear-gradient(135deg, #1e2460 0%, #2d3484 50%, #3b43a4 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {dataRifa.rutaArchivo ? (
                    <Box
                      component="img"
                      src={
                        dataRifa.rutaArchivo.startsWith('http')
                          ? dataRifa.rutaArchivo
                          : `${(process.env.REACT_APP_URL_FILES || 'https://archivostest.rauhtech.com/').replace(/\/$/, '')}/${dataRifa.rutaArchivo.replace(/^\//, '')}`
                      }
                      alt={dataRifa.titulo}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: 'brightness(0.9)',
                        transition: 'transform 0.4s ease',
                        '&:hover': {
                          transform: 'scale(1.02)',
                        },
                      }}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.4)', p: 2 }}>
                      <Icon sx={{ fontSize: 48, opacity: 0.6 }}>confirmation_number</Icon>
                    </Box>
                  )}

                  {/* Overlay Gradiente Oscuro con Título */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      p: { xs: 1.8, sm: 2.2 },
                      background: 'linear-gradient(180deg, transparent 0%, rgba(15, 23, 42, 0.92) 100%)',
                      color: 'white',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 900,
                        fontSize: { xs: '1.15rem', sm: '1.35rem' },
                        lineHeight: 1.25,
                        textShadow: '0 2px 10px rgba(0,0,0,0.7)',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {dataRifa.titulo}
                    </Typography>
                  </Box>
                </Box>

                {/* 2. Cuerpo de Información en el orden solicitado */}
                <Box sx={{ p: { xs: 2, sm: 2.2 }, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                  {/* 1. GRAN PREMIO */}
                  {dataRifa.premio && (
                    <Box
                      sx={{
                        p: 1.3,
                        borderRadius: '10px',
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.08)' : 'rgba(45, 52, 132, 0.04)',
                        border: theme.palette.mode === 'dark' ? '1.2px solid rgba(99, 102, 241, 0.25)' : '1.2px solid rgba(45, 52, 132, 0.14)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.4,
                      }}
                    >
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: '8px',
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.16)' : 'rgba(45, 52, 132, 0.08)',
                          color: theme.palette.primary.light,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 22, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main }}>emoji_events</Icon>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 800, textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.5, display: 'block' }}>
                          GRAN PREMIO
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 900, color: theme.palette.text.primary, fontSize: '0.98rem', lineHeight: 1.2, mt: 0.1 }}>
                          {dataRifa.premio}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* NÚMEROS GANADORES (si existen) */}
                  {numerosGanadores.length > 0 && (
                    <Box
                      sx={{
                        p: 1.4,
                        borderRadius: '10px',
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.08)' : 'rgba(245, 158, 11, 0.06)',
                        border: theme.palette.mode === 'dark' ? '1.5px solid rgba(245, 158, 11, 0.3)' : '1.5px solid rgba(245, 158, 11, 0.25)',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Icon sx={{ color: '#f59e0b', fontSize: 20 }}>emoji_events</Icon>
                        <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 900, textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5 }}>
                          NÚMEROS GANADORES ({numerosGanadores.length})
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                        {numerosGanadores.map((item) => (
                          <Chip
                            key={item.id || item.numero}
                            icon={<span style={{ fontSize: 14, marginLeft: 6 }}>🏆</span>}
                            label={item.numero}
                            size="small"
                            sx={{
                              bgcolor: '#f59e0b',
                              color: '#ffffff',
                              fontWeight: 900,
                              borderRadius: '6px',
                              fontSize: '0.84rem',
                              boxShadow: '0 2px 6px rgba(245, 158, 11, 0.3)',
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* 2. FECHA DEL SORTEO */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.4,
                      p: 1.2,
                      borderRadius: '10px',
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
                      border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                    }}
                  >
                    <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(45, 52, 132, 0.08)', color: theme.palette.primary.main, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon sx={{ fontSize: 20, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main }}>event</Icon>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 800, textTransform: 'uppercase', fontSize: '0.66rem', letterSpacing: 0.5, display: 'block' }}>
                        FECHA DEL SORTEO
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 800, fontSize: '0.84rem', mt: 0.1 }}>
                        {moment(dataRifa.fecha_sorteo).format('dddd, DD [de] MMMM [del] YYYY [a las] hh:mm a')}
                      </Typography>
                    </Box>
                  </Box>

                  {/* 3. VALOR POR BOLETO */}
                  <Box
                    sx={{
                      p: 1.3,
                      borderRadius: '10px',
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.08)' : 'rgba(45, 52, 132, 0.04)',
                      border: theme.palette.mode === 'dark' ? '1.2px solid rgba(99, 102, 241, 0.25)' : '1.2px solid rgba(45, 52, 132, 0.14)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.4,
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '8px',
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#ffffff',
                        border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e2e8f0',
                        color: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Icon sx={{ fontSize: 22, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main }}>payments</Icon>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 800, textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.5, display: 'block' }}>
                        VALOR POR BOLETO
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 900, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, fontSize: '1.15rem', lineHeight: 1.2, mt: 0.1 }}>
                        {formatPrice(dataRifa.precio)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* 4. MÉTODO DE PAGO */}
                  {(dataRifa.metodo_pago || dataRifa.metodos_pago) && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.4,
                        p: 1.2,
                        borderRadius: '10px',
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
                        border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                      }}
                    >
                      <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(45, 52, 132, 0.08)', color: theme.palette.primary.main, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2 }}>
                        <Icon sx={{ fontSize: 20, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main }}>account_balance_wallet</Icon>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 800, textTransform: 'uppercase', fontSize: '0.66rem', letterSpacing: 0.5, display: 'block' }}>
                          MÉTODO DE PAGO
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600, mt: 0.1, fontSize: '0.84rem', lineHeight: 1.45, whiteSpace: 'pre-line' }}>
                          {dataRifa.metodo_pago || dataRifa.metodos_pago}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* 5. LOTERÍA */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.4,
                      p: 1.2,
                      borderRadius: '10px',
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
                      border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                    }}
                  >
                    <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(45, 52, 132, 0.08)', color: theme.palette.primary.main, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon sx={{ fontSize: 20, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main }}>casino</Icon>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 800, textTransform: 'uppercase', fontSize: '0.66rem', letterSpacing: 0.5, display: 'block' }}>
                        LOTERÍA
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 800, fontSize: '0.86rem', mt: 0.1 }}>
                        {dataRifa.nombre_loteria || dataRifa.nombreLoteria || 'Sorteo Interno'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* 6. DESCRIPCIÓN (último elemento) */}
                  {dataRifa.descripcion && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.4,
                        p: 1.2,
                        borderRadius: '10px',
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
                        border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                      }}
                    >
                      <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(45, 52, 132, 0.08)', color: theme.palette.primary.main, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2 }}>
                        <Icon sx={{ fontSize: 20, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main }}>notes</Icon>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 800, textTransform: 'uppercase', fontSize: '0.66rem', letterSpacing: 0.5, display: 'block' }}>
                          DESCRIPCIÓN
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500, mt: 0.1, fontSize: '0.84rem', lineHeight: 1.45 }}>
                          {dataRifa.descripcion}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Fade>
          </Grid>

          {/* ================= COLUMNA DERECHA: TABLERO INTERACTIVO ================= */}
          <Grid item xs={12} lg={7}>
            <Grow in timeout={600}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '14px',
                  overflow: 'hidden',
                  bgcolor: theme.palette.background.paper,
                  border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                  boxShadow: theme.palette.mode === 'dark' ? '0 8px 24px rgba(0, 0, 0, 0.4)' : '0 8px 24px rgba(45, 52, 132, 0.04)',
                }}
              >
                {/* Cabecera del Tablero */}
                <Box
                  sx={{
                    background: theme.palette.mode === 'dark' ? '#1e293b' : '#2d3484',
                    color: 'white',
                    p: { xs: 1.4, sm: 1.8 },
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      width: 140,
                      height: 140,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.06)',
                      top: -40,
                      right: -20,
                      pointerEvents: 'none',
                    }}
                  />

                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 900,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontSize: { xs: '1.05rem', sm: '1.18rem' },
                          letterSpacing: '-0.01em',
                        }}
                      >
                        <Icon sx={{ fontSize: '20px !important' }}>dialpad</Icon> Tablero
                      </Typography>

                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.7,
                          px: 1.2,
                          py: 0.3,
                          bgcolor: 'rgba(255, 255, 255, 0.15)',
                          border: '1px solid rgba(255, 255, 255, 0.25)',
                          borderRadius: '6px',
                          backdropFilter: 'blur(4px)',
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: dataRifa.activo === '1' ? '#10b981' : '#ef4444',
                            boxShadow: dataRifa.activo === '1' ? '0 0 6px #10b981' : '0 0 6px #ef4444',
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 800,
                            color: '#ffffff',
                            textTransform: 'uppercase',
                            letterSpacing: 0.6,
                            fontSize: '0.7rem',
                          }}
                        >
                          {dataRifa.activo === '1' ? 'Activo' : 'Inactivo'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Componente Tablero */}
                <Box sx={{ p: { xs: 1.5, sm: 2 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Tablero
                    key={tableroKey}
                    rifa={dataRifa}
                    onReservaSuccess={handleReservaSuccess}
                  />
                </Box>
              </Paper>
            </Grow>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default MiRifa;
